import { useCallback, useId, useMemo, useState } from "react";
import { CornerDownLeft, Library, Lightbulb, Sparkles } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import { cn } from "../common/cn";
import { Card, CardHeader } from "../common/Card";
import { ScenarioLibrary } from "../scenario/ScenarioLibrary";
import { QuestionQualityGate } from "./QuestionQualityGate";
import { t, tr } from "../../i18n";

const MIN_LEN = 12;
const MAX_LEN = 1500;

const EXAMPLES: readonly string[] = [
  tr.examples.e1,
  tr.examples.e2,
  tr.examples.e3,
  tr.examples.e4,
];

export interface QueryComposerSubmitSeed {
  isTrusted: boolean;
  timeStamp: number;
}

interface QueryComposerProps {
  disabled?: boolean;
  /** Triggered when user presses Cmd/Ctrl+Enter (passes the underlying event seed). */
  onSubmitShortcut?: (seed: QueryComposerSubmitSeed) => void;
}

function counterTone(count: number): string {
  if (count > MAX_LEN) return "text-rose-300";
  if (count > MAX_LEN - 100) return "text-amber-300";
  if (count >= MIN_LEN) return "text-emerald-300";
  return "text-fg-subtle";
}

export function QueryComposer({ disabled, onSubmitShortcut }: QueryComposerProps) {
  const id = useId();
  const query = useDebateStore((s) => s.query);
  const setQuery = useDebateStore((s) => s.setQuery);
  const status = useDebateStore((s) => s.status);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const isLocked = disabled || status === "running" || status === "provisioning";
  const trimmed = query.trim().length;
  const valid = trimmed >= MIN_LEN;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && valid && !isLocked) {
        e.preventDefault();
        onSubmitShortcut?.({
          isTrusted: e.nativeEvent.isTrusted,
          timeStamp: e.nativeEvent.timeStamp,
        });
      }
    },
    [valid, isLocked, onSubmitShortcut],
  );

  const fillExample = useCallback(
    (text: string) => {
      if (isLocked) return;
      setQuery(text);
    },
    [isLocked, setQuery],
  );

  const helpText = useMemo(
    () =>
      valid
        ? t("setup.queryHelpMax", { count: trimmed, max: MAX_LEN })
        : t("setup.queryHelpMin", { min: MIN_LEN }),
    [valid, trimmed],
  );

  return (
    <Card glow tone="elevated" aria-labelledby={`${id}-label`}>
      <CardHeader
        icon={<Sparkles className="h-4 w-4" aria-hidden />}
        title={<span id={`${id}-label`}>{t("setup.queryLabel")}</span>}
        subtitle={t("setup.sectionTitle")}
        actions={
          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            disabled={isLocked}
            className={cn(
              "focus-ring inline-flex items-center gap-1.5 rounded-full",
              "border border-[hsl(var(--accent)/0.4)] bg-[hsl(var(--accent)/0.1)]",
              "px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-accent-200",
              "transition hover:bg-[hsl(var(--accent)/0.18)] hover:text-accent-100",
              isLocked && "cursor-not-allowed opacity-50",
            )}
          >
            <Library className="h-3.5 w-3.5" aria-hidden />
            Senaryo Kütüphanesi
          </button>
        }
      />
      <ScenarioLibrary open={libraryOpen} onOpenChange={setLibraryOpen} />
      <div className="flex flex-col gap-3 p-4 sm:p-5">
        <div className="relative">
          <textarea
            id={id}
            className={cn(
              "min-h-[10.5rem] w-full resize-y rounded-xl",
              "border border-line-soft/70 bg-surface-0/60 px-3.5 py-3",
              "text-fluid-base leading-relaxed text-fg-strong placeholder:text-fg-subtle",
              "shadow-[inset_0_1px_0_hsl(0_0%_100%/0.04)] focus-ring",
              "transition-colors duration-150",
              "focus:border-[hsl(var(--accent)/0.6)]",
              isLocked && "cursor-not-allowed opacity-60",
            )}
            placeholder={t("setup.queryPlaceholder")}
            value={query}
            maxLength={MAX_LEN}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLocked}
            aria-describedby={`${id}-meta ${id}-shortcut`}
          />
          <span
            id={`${id}-shortcut`}
            className="pointer-events-none absolute bottom-2 right-3 hidden items-center gap-1 text-[10px] text-fg-subtle sm:inline-flex"
          >
            <kbd className="rounded border border-line-soft/80 bg-surface-2/70 px-1 py-0.5 font-mono">
              Ctrl
            </kbd>
            <span>+</span>
            <kbd className="rounded border border-line-soft/80 bg-surface-2/70 px-1 py-0.5 font-mono">
              <CornerDownLeft className="inline h-2.5 w-2.5" aria-hidden />
            </kbd>
          </span>
        </div>

        <div
          id={`${id}-meta`}
          className="flex items-center justify-between gap-3 text-xs"
        >
          <span className={cn("transition-colors", counterTone(trimmed))}>{helpText}</span>
          <span className="font-mono text-[11px] tabular-nums text-fg-subtle">
            {trimmed}/{MAX_LEN}
          </span>
        </div>

        {/* Örnekler yalnızca boş tuvalde işe yarar. Soru yazıldıktan sonra
            operatörün odağı netleştirmeye kayar; şablon rozetleri orada
            sadece gürültüdür ve kalite kapısını aşağı iter. */}
        {trimmed === 0 ? (
          <div className="flex flex-col gap-2 border-t border-line-soft/50 pt-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-fg-subtle">
              <Lightbulb className="h-3.5 w-3.5 text-accent-300" aria-hidden />
              {t("setup.queryExamplesTitle")}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={isLocked}
                  onClick={() => fillExample(ex)}
                  className={cn(
                    "max-w-full truncate rounded-full border border-line-soft/60 bg-surface-2/50 px-2.5 py-1",
                    "text-left text-xs text-fg-muted transition focus-ring",
                    "hover:border-[hsl(var(--accent)/0.55)] hover:bg-surface-2/80 hover:text-fg-strong",
                    isLocked && "cursor-not-allowed opacity-50",
                  )}
                  title={ex}
                >
                  <span className="line-clamp-1">{ex}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Soru kalite kapısı — AYRI BİR KART DEĞİL. Soruyu yazmak ile
            soruyu netleştirmek tek bir iştir; ikisini iki kutuya bölmek
            adım 1'i "iki görev" gibi gösteriyordu. Kapı bloklamaz:
            ölçer, gerekçelendirir ve şıklı bir yeniden yazım önerir. */}
        <QuestionQualityGate />
      </div>
    </Card>
  );
}
